import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CheckCircle2, ArrowRight, FileText, Users, Briefcase, ShieldCheck, Clock, IndianRupee, Percent } from 'lucide-react';
import { PRODUCTS } from '../data/products';
import { INDUSTRIES } from '../data/industries';

const SITE_URL = 'https://loanyardcapital.com';

const DEFAULT_PROCESS = [
  ['Consultation', 'We understand your requirement, financials and timelines.'],
  ['Structuring', 'Our analysts prepare a lender-grade proposal.'],
  ['Placement', 'Competitive placement across best-fit lenders.'],
  ['Negotiation', 'Amount, pricing, tenure and covenants negotiated.'],
  ['Disbursement', 'Documentation to disbursal, managed end to end.']
];

const DEFAULT_DOCUMENTS = [
  'KYC of promoters and business entity',
  'Last 2–3 years financial statements / ITRs',
  'Last 12 months bank statements',
  'GST returns where applicable',
  'Existing loan sanction letters and repayment track',
  'Requirement-specific documents (we guide you)'
];

const scrollToContact = () => {
  window.location.href = '/#contact';
};

export default function ProductPage({ slug }) {
  const p = PRODUCTS[slug];
  const processSteps = p?.process || DEFAULT_PROCESS;
  const documents = p?.documents || DEFAULT_DOCUMENTS;

  useEffect(() => {
    if (!p) return;
    window.scrollTo(0, 0);
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: p.faqs.map(([q, a]) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a }
      }))
    };
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: p.name, item: `${SITE_URL}/${slug}` }
      ]
    };
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'product-schema';
    el.text = JSON.stringify([faqSchema, breadcrumbSchema]);
    document.head.appendChild(el);
    return () => { document.getElementById('product-schema')?.remove(); };
  }, [p, slug]);

  if (!p) return null;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <WhatsAppButton />

      {/* Hero */}
      <section className="pt-28 sm:pt-32 md:pt-36 lg:pt-40 pb-12 sm:pb-16 bg-slate-950 text-white relative overflow-hidden" data-testid="product-hero">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(16,185,129,0.15),transparent_60%)]"></div>
        <div className="container mx-auto px-4 relative">
          <p className="text-emerald-400 text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] mb-3 sm:mb-4">
            <Link to="/" className="text-slate-400 hover:text-emerald-300">Home</Link> <span className="text-slate-600">/</span> {p.name}
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold leading-[1.1] sm:leading-[1.1] max-w-4xl mb-4 sm:mb-6 text-white">{p.heroTitle}</h1>
          <p className="text-base sm:text-lg text-slate-300 max-w-3xl leading-relaxed mb-6 sm:mb-8">{p.heroSub}</p>
          <div className="flex flex-col sm:flex-row gap-4 mb-10">
            <Button onClick={scrollToContact} size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-6 h-auto text-lg" data-testid="product-cta-primary">
              Get a Free Consultation <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a href="https://wa.me/919892759155" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white px-8 py-6 h-auto text-lg w-full">
                WhatsApp an Expert
              </Button>
            </a>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
              <IndianRupee className="w-5 h-5 text-emerald-400 mb-2" />
              <p className="text-xs text-slate-400 uppercase tracking-wider">Funding Amount</p>
              <p className="text-lg font-bold">{p.amount}</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
              <Clock className="w-5 h-5 text-emerald-400 mb-2" />
              <p className="text-xs text-slate-400 uppercase tracking-wider">Approval Timeline</p>
              <p className="text-lg font-bold">{p.timeline}</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
              <Percent className="w-5 h-5 text-emerald-400 mb-2" />
              <p className="text-xs text-slate-400 uppercase tracking-wider">Pricing</p>
              <p className="text-lg font-bold">{p.rate}</p>
            </div>
          </div>
          <Link to="/calculators/emi" className="inline-flex items-center gap-1.5 text-emerald-300 hover:text-emerald-200 text-sm font-semibold mt-5 transition-colors" data-testid="product-emi-calc-link">
            Estimate your EMI with our free calculator <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">About {p.name}</h2>
          {p.intro.map((para, i) => (
            <p key={`intro-${i}`} className="text-lg text-slate-600 leading-relaxed mb-5">{para}</p>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-10 text-center">Key Benefits</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {p.benefits.map((b, i) => (
              <div key={`ben-${i}`} className="flex items-start gap-3 p-5 bg-white rounded-xl border border-slate-200 shadow-sm">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700 font-medium">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Should Apply + Eligibility */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 max-w-5xl grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <Users className="w-6 h-6 text-emerald-600" /> Who Should Apply
            </h2>
            <ul className="space-y-3">
              {p.whoShouldApply.map((w, i) => (
                <li key={`who-${i}`} className="flex items-start gap-2 text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2.5 flex-shrink-0"></span>{w}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-emerald-600" /> Eligibility
            </h2>
            <ul className="space-y-3">
              {p.eligibility.map((e, i) => (
                <li key={`eli-${i}`} className="flex items-start gap-2 text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2.5 flex-shrink-0"></span>{e}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Documents + Industries */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 max-w-5xl grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <FileText className="w-6 h-6 text-emerald-600" /> Documents Required
            </h2>
            <ul className="space-y-3">
              {documents.map((d, i) => (
                <li key={`doc-${i}`} className="flex items-start gap-2 text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2.5 flex-shrink-0"></span>{d}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-emerald-600" /> Industries Served
            </h2>
            <div className="flex flex-wrap gap-2">
              {p.industries.map((ind) => (
                <span key={ind} className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium border border-emerald-100">{ind}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-slate-950 text-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold mb-12 text-center">Our Process</h2>
          <div className="grid md:grid-cols-5 gap-6">
            {processSteps.map(([title, desc], i) => (
              <div key={`step-${i}`} className="text-center">
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-4">{i + 1}</div>
                <h3 className="font-bold mb-2">{title}</h3>
                <p className="text-sm text-slate-400">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why LOANYARD */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-10">Why LOANYARD CAPITAL</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[['15+ Years', 'Advisory Experience'], ['₹500+ Cr', 'Deals Facilitated'], ['500+', 'Corporate Clients'], ['Pan-India', 'Coverage']].map(([v, l]) => (
              <div key={l} className="p-6 bg-white rounded-xl border border-slate-200 shadow-sm">
                <p className="text-2xl font-bold text-emerald-600">{v}</p>
                <p className="text-sm text-slate-600 mt-1">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-10 text-center">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-3" data-testid="product-faq">
            {p.faqs.map(([q, a], i) => (
              <AccordionItem key={`faq-${i}`} value={`faq-${i}`} className="bg-white border border-slate-200 rounded-xl px-6">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-emerald-700 py-5">{q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-5">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Relevant Industries */}
      {p.industries && p.industries.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 text-center">Industries We Serve</h2>
            <p className="text-slate-600 text-center mb-8 max-w-2xl mx-auto">
              {p.name} is particularly relevant for these business sectors:
            </p>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
              {p.industries.map((indName) => {
                const indSlug = Object.keys(INDUSTRIES).find(key => 
                  INDUSTRIES[key].name.toLowerCase() === indName.toLowerCase() ||
                  INDUSTRIES[key].name.includes(indName) ||
                  indName.includes(INDUSTRIES[key].name)
                );
                if (!indSlug) {
                  return (
                    <div key={indName} className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                      <Briefcase className="w-5 h-5 text-emerald-600 mb-2" />
                      <p className="font-semibold text-slate-900 text-sm">{indName}</p>
                    </div>
                  );
                }
                return (
                  <Link key={indSlug} to={`/industries/${indSlug}`} className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm hover:border-emerald-400 hover:shadow-md transition-all group">
                    <Briefcase className="w-5 h-5 text-emerald-600 mb-2" />
                    <p className="font-semibold text-slate-900 group-hover:text-emerald-700 text-sm">{INDUSTRIES[indSlug].name}</p>
                    <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">View Industry <ArrowRight className="w-3 h-3" /></p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Related */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Related Funding Solutions</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {p.related.filter((r) => PRODUCTS[r]).map((r) => (
              <Link key={r} to={`/${r}`} className="p-5 bg-white rounded-xl border border-slate-200 shadow-sm hover:border-emerald-400 hover:shadow-md transition-all group" data-testid={`related-${r}`}>
                <p className="font-semibold text-slate-900 group-hover:text-emerald-700">{PRODUCTS[r].name}</p>
                <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">Explore <ArrowRight className="w-3 h-3" /></p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-slate-950 text-white text-center">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold mb-4">Ready to Discuss Your {p.name} Requirement?</h2>
          <p className="text-slate-300 mb-8">Speak to a corporate finance specialist today. No fees for consultation — only structured, honest advisory.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={scrollToContact} size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-6 h-auto text-lg" data-testid="product-cta-final">
              Apply Now <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a href="tel:+919892759155">
              <Button size="lg" variant="outline" className="border border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white px-8 py-6 h-auto text-lg w-full">
                Call +91 98927 59155
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
